import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import Button from './ui/Button';
import { Menu, X } from 'lucide-react';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-light shadow-md py-3' : 'bg-transparent py-5'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="z-20">
            <Logo />
          </Link>
          
          <button 
            className="md:hidden z-20 text-dark"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>

          <nav className="hidden md:flex items-center space-x-8">
            <NavLinks location={location} />
            <Button
              variant="secondary"
              className="ml-4"
              onClick={() => window.open('https://calendly.com/rentsparks', '_blank')}
            >
              Let's Chat!
            </Button>
          </nav>

          <div className={`fixed inset-0 bg-light z-10 flex flex-col justify-center items-center transition-transform duration-300 ease-in-out ${
            mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
          } md:hidden`}>
            <div className="flex flex-col items-center space-y-6">
              <NavLinks 
                mobile 
                location={location}
                onClick={() => setMobileMenuOpen(false)} 
              />
              <Button
                variant="secondary"
                className="mt-8"
                onClick={() => {
                  window.open('https://calendly.com/rentsparks', '_blank');
                  setMobileMenuOpen(false);
                }}
              >
                Schedule Consultation
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

interface NavLinksProps {
  mobile?: boolean;
  onClick?: () => void;
  location: { pathname: string };
}

const NavLinks: React.FC<NavLinksProps> = ({ mobile = false, onClick, location }) => {
  const links = [
    { name: 'Home', path: '/#home' },
    { name: 'Services', path: '/#services' },
    { name: 'About', path: '/#about' },
    { name: 'Pricing', path: '/#pricing' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/#contact' },
  ];

  const handleClick = (path: string) => {
    if (onClick) onClick();
    
    if (path.startsWith('/#') && location.pathname !== '/') {
      window.location.href = path;
    } else if (path.startsWith('/#')) {
      document.querySelector(path.substring(1))?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {links.map((link) => (
        <Link
          key={link.name}
          to={link.path.startsWith('/#') ? '/' : link.path}
          className={`font-abadi text-dark hover:text-secondary transition-colors duration-200 ${
            mobile ? 'text-xl py-3' : 'text-lg'
          }`}
          onClick={() => handleClick(link.path)}
        >
          {link.name}
        </Link>
      ))}
    </>
  );
};

export default Header;